import React from 'react'
import {
    Document,
    Page,
    StyleSheet,
    Text as PDFText,
    View,
} from '@react-pdf/renderer'
import { formatDistanceToNowStrict } from 'date-fns'

import { Text } from '~components/shared/create-pdf/pdf-text'
import {
    ClinicFieldsFragment,
    PatientFieldsFragment,
    PatientVaccinationRecordFieldsFragment,
} from '~graphql/generated/graphql'
import { formatDate, getCleanEnum } from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'

interface Props {
    clinic: ClinicFieldsFragment | undefined
    patient: PatientFieldsFragment
    vaccines: PatientVaccinationRecordFieldsFragment[]
}

export default function VaccinationRecordsPDFDocument({
    clinic,
    patient,
    vaccines,
}: Props) {
    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.section}>
                    <ClinicHeader clinic={clinic} />
                    <Text size="lg" fontWeight="bold" color="#13D879">
                        {`Vaccination Record for ${patient.name} - ${getOwnerName(patient)}`}
                    </Text>
                    <PatientHighlights patient={patient} />
                    <VaccinationRecordsTable
                        clinic={clinic}
                        vaccines={vaccines}
                    />
                </View>
            </Page>
        </Document>
    )
}

function ClinicHeader({ clinic }: { clinic: ClinicFieldsFragment | undefined }) {
    const contactLines = [
        getClinicContactLine(clinic),
        clinic?.email,
    ].filter((line): line is string => Boolean(line))

    return (
        <View style={styles.clinicHeader}>
            <Text size="md" fontWeight="bold">
                {clinic?.name ?? ''}
            </Text>
            {contactLines.map(line => (
                <Text key={line} size="xs">
                    {line}
                </Text>
            ))}
        </View>
    )
}

function PatientHighlights({ patient }: { patient: PatientFieldsFragment }) {
    return (
        <View style={styles.highlightsSection}>
            <Text size="sm" fontWeight="bold">
                Patient's Current Highlights
            </Text>
            <View style={styles.highlightsTable}>
                <HighlightRow
                    label="Client"
                    value={`${getOwnerName(patient)}${getClientPhoneText(patient)}`}
                />
                <HighlightRow
                    label="Patient"
                    value={`${patient.name} (${getPatientDescription(patient)})`}
                />
                <HighlightRow
                    label="Breed"
                    value={getBreedDescription(patient)}
                />
                <HighlightRow label="Status" value={getPatientStatus(patient)} />
                <HighlightRow label="Weight" value={getPatientWeight(patient)} />
            </View>
        </View>
    )
}

function HighlightRow({ label, value }: { label: string; value: string }) {
    return (
        <View style={styles.highlightRow}>
            <View style={styles.highlightLabel}>
                <Text size="xs" fontWeight="semibold">
                    {label}
                </Text>
            </View>
            <View style={styles.highlightValue}>
                <Text size="xs">{value}</Text>
            </View>
        </View>
    )
}

function VaccinationRecordsTable({
    clinic,
    vaccines,
}: {
    clinic: ClinicFieldsFragment | undefined
    vaccines: PatientVaccinationRecordFieldsFragment[]
}) {
    if (vaccines.length === 0) {
        return (
            <View style={styles.emptyState}>
                <Text size="sm" color="#718096">
                    No vaccination records found.
                </Text>
            </View>
        )
    }

    return (
        <View style={styles.vaccinationSection}>
            <Text size="sm" fontWeight="bold">
                Vaccination Record
            </Text>
            <View style={styles.table}>
            <View style={styles.tableHead}>
                <HeaderCell>Date Vaccinated</HeaderCell>
                <HeaderCell flex={1.8}>Vaccine</HeaderCell>
                <HeaderCell>Tag/Serial</HeaderCell>
                <HeaderCell>Expires</HeaderCell>
                <HeaderCell flex={1.8}>Practice</HeaderCell>
                <HeaderCell>Manufacturer</HeaderCell>
            </View>
            {vaccines.map((vaccine, index) => (
                <View
                    key={vaccine.id}
                    style={{
                        ...styles.tableRow,
                        ...(index % 2 === 0 ? styles.altRow : {}),
                    }}
                >
                    <BodyCell>{formatOptionalDate(vaccine.administered_date)}</BodyCell>
                    <BodyCell flex={1.8}>{vaccine.vaccine_name || 'N/A'}</BodyCell>
                    <BodyCell>{getTagSerial(vaccine)}</BodyCell>
                    <BodyCell>{formatOptionalDate(vaccine.due_date)}</BodyCell>
                    <BodyCell flex={1.8}>{clinic?.name ?? 'N/A'}</BodyCell>
                    <BodyCell>{vaccine.manufacturer || 'N/A'}</BodyCell>
                </View>
            ))}
            </View>
        </View>
    )
}

function HeaderCell({
    children,
    flex = 1,
}: {
    children: string
    flex?: number
}) {
    return (
        <View style={{ ...styles.tableCell, flex }}>
            <PDFText style={styles.tableHeaderText}>{children.toUpperCase()}</PDFText>
        </View>
    )
}

function BodyCell({
    children,
    flex = 1,
}: {
    children: string
    flex?: number
}) {
    return (
        <View style={{ ...styles.tableCell, flex }}>
            <Text size="xs">{children}</Text>
        </View>
    )
}

function formatOptionalDate(date: number | null | undefined) {
    return date ? formatDate(date) : 'N/A'
}

function getClinicContactLine(clinic: ClinicFieldsFragment | undefined) {
    const phone = formatPhoneReadable(clinic?.phone)

    return phone ? `W: ${phone}` : ''
}

function getClientPhoneText(patient: PatientFieldsFragment) {
    const phoneText = formatPhoneReadable(
        patient.owner?.phone,
        patient.owner?.address?.country,
    )

    return phoneText ? ` (${phoneText})` : ''
}

function getOwnerName(patient: PatientFieldsFragment) {
    return [patient.owner?.first_name, patient.owner?.last_name]
        .filter(Boolean)
        .join(' ') || 'N/A'
}

function getPatientDescription(patient: PatientFieldsFragment) {
    const parts = [
        patient.gender ? getCleanEnum(patient.gender) : '',
        patient.fixed_status ? getCleanEnum(patient.fixed_status) : '',
        patient.birthday ? formatDistanceToNowStrict(patient.birthday) : '',
    ].filter(Boolean)

    return parts.length ? parts.join(' - ') : 'N/A'
}

function getBreedDescription(patient: PatientFieldsFragment) {
    return [
        patient.clinic_species?.species,
        patient.clinic_breed?.description,
    ]
        .filter(Boolean)
        .join(' - ') || 'N/A'
}

function getPatientStatus(patient: PatientFieldsFragment) {
    if (patient.is_deceased) {
        return 'Deceased'
    }

    return patient.is_active ? 'Active' : 'Inactive'
}

function getPatientWeight(patient: PatientFieldsFragment) {
    if (!patient.weight) {
        return 'N/A'
    }

    return `${patient.weight} ${patient.clinic_species?.weight_key ?? ''}`.trim()
}

function getTagSerial(vaccine: PatientVaccinationRecordFieldsFragment) {
    return vaccine.rabies_tag || vaccine.lot_number || 'N/A'
}

const styles = StyleSheet?.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
    },
    section: {
        margin: 32,
        paddingHorizontal: 8,
        paddingVertical: 4,
    },
    clinicHeader: {
        marginBottom: 18,
    },
    highlightsSection: {
        marginTop: 14,
        marginBottom: 16,
    },
    highlightsTable: {
        marginTop: 6,
    },
    highlightRow: {
        flexDirection: 'row',
        marginBottom: 3,
    },
    highlightLabel: {
        width: 86,
    },
    highlightValue: {
        flex: 1,
    },
    vaccinationSection: {
        marginTop: 4,
    },
    table: {
        width: '100%',
        marginTop: 6,
    },
    tableHead: {
        flexDirection: 'row',
        borderTopWidth: 1,
        borderTopColor: '#1A202C',
        borderTopStyle: 'solid',
        borderBottomWidth: 1,
        borderBottomColor: '#1A202C',
        borderBottomStyle: 'solid',
        alignItems: 'center',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
        borderBottomStyle: 'solid',
        alignItems: 'center',
    },
    altRow: {
        backgroundColor: '#F7FAFC',
    },
    tableCell: {
        flex: 1,
        paddingVertical: 5,
        paddingHorizontal: 2,
    },
    tableHeaderText: {
        color: '#4A5468',
        fontFamily: 'Inter',
        fontSize: '7px',
        fontWeight: 600,
        lineHeight: 1,
    },
    emptyState: {
        padding: 16,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderStyle: 'solid',
        borderRadius: 8,
    },
})
